<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { storeToRefs } from 'pinia';
  import Button from 'primevue/button';
  import Menu from 'primevue/menu';
  import Avatar from 'primevue/avatar';
  import { GraduationCap, Menu as MenuIcon, X } from 'lucide-vue-next';
  import { useAuthStore } from '@/stores/auth';
  import { useLogoutMutation } from '@/modules/auth/api';
  import NotificationBell from '@/modules/notifications/components/NotificationBell.vue';

  const { t } = useI18n();
  const router = useRouter();
  const auth = useAuthStore();
  const { isAuthenticated, user, role } = storeToRefs(auth);
  const logoutMutation = useLogoutMutation();

  interface NavLink {
    to: string;
    labelKey: string;
  }

  const roleNavLinks = computed<NavLink[]>(() => {
    if (role.value === 'applicant') {
      return [{ to: '/bewerbung', labelKey: 'nav.my_application' }];
    }
    if (role.value === 'administrator') {
      return [
        { to: '/admin/bewerber', labelKey: 'nav.applicants' },
        { to: '/admin/curriculums', labelKey: 'nav.curriculums' },
        { to: '/admin/subjects', labelKey: 'nav.subjects' },
        { to: '/admin/teachers', labelKey: 'nav.teachers' },
        { to: '/admin/document-templates', labelKey: 'nav.templates' },
        { to: '/admin/config', labelKey: 'nav.settings' },
      ];
    }
    if (role.value === 'teacher') {
      return [
        { to: '/lehrer/pruefungen', labelKey: 'nav.my_exams' },
        { to: '/lehrer/noten-erfassen', labelKey: 'nav.grading' },
      ];
    }
    if (role.value === 'student') {
      return [
        { to: '/student/dashboard', labelKey: 'nav.my_studies' },
        { to: '/student/pruefungen', labelKey: 'nav.exams' },
        { to: '/student/noten', labelKey: 'nav.grades' },
        { to: '/student/zertifikate', labelKey: 'nav.certificates' },
      ];
    }
    return [];
  });

  const mobileOpen = ref(false);
  const accountMenu = ref<InstanceType<typeof Menu> | null>(null);

  const initials = computed(() => {
    if (!user.value) return '';
    const first = user.value.first_name?.[0] ?? '';
    const last = user.value.last_name?.[0] ?? '';
    return `${first}${last}`.toUpperCase();
  });

  const fullName = computed(() => {
    if (!user.value) return '';
    return `${user.value.first_name} ${user.value.last_name}`.trim();
  });

  async function doLogout() {
    try {
      await logoutMutation.mutateAsync();
    } catch {
      auth.clearAuth();
    }
    mobileOpen.value = false;
    await router.push('/login');
  }

  const accountMenuItems = computed(() => [
    {
      label: t('nav.logout'),
      icon: 'pi pi-sign-out',
      command: () => {
        void doLogout();
      },
    },
  ]);

  function openAccountMenu(event: Event) {
    accountMenu.value?.toggle(event);
  }
</script>

<template>
  <div class="min-h-screen flex flex-col bg-surface-50">
    <header class="border-b border-surface-200 bg-white">
      <div class="page-container flex items-center justify-between py-4">
        <RouterLink to="/" class="flex items-center gap-3" @click="mobileOpen = false">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-600 text-white"
          >
            <GraduationCap :size="20" />
          </div>
          <div class="leading-tight">
            <div class="text-base font-semibold text-surface-900">{{ t('app.title') }}</div>
            <div class="hidden text-xs text-surface-500 sm:block">
              {{ t('app.subtitle') }}
            </div>
          </div>
        </RouterLink>

        <!-- Desktop nav -->
        <nav class="hidden items-center gap-2 md:flex" data-testid="nav-desktop">
          <template v-if="!isAuthenticated">
            <RouterLink to="/login">
              <Button :label="t('nav.login')" severity="secondary" outlined size="small" />
            </RouterLink>
            <RouterLink to="/register">
              <Button :label="t('nav.register')" severity="primary" size="small" />
            </RouterLink>
          </template>
          <template v-else>
            <RouterLink
              v-for="(link, idx) in roleNavLinks"
              :key="link.to"
              :to="link.to"
              class="rounded-lg px-3 py-1 text-sm font-medium text-surface-700 hover:bg-surface-100"
              :data-testid="idx === 0 ? 'role-nav-link' : undefined"
            >
              {{ t(link.labelKey) }}
            </RouterLink>
            <NotificationBell />
            <button
              type="button"
              class="flex items-center gap-2 rounded-lg px-2 py-1 text-sm font-medium text-surface-800 hover:bg-surface-100"
              :aria-label="t('nav.account_menu')"
              data-testid="account-trigger"
              @click="openAccountMenu"
            >
              <Avatar :label="initials" shape="circle" class="bg-primary-600 text-white" />
              <span class="hidden lg:inline">{{ fullName }}</span>
            </button>
            <Menu ref="accountMenu" :model="accountMenuItems" :popup="true" />
          </template>
        </nav>

        <!-- Mobile toggle -->
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-lg p-2 text-surface-700 hover:bg-surface-100 md:hidden"
          :aria-label="mobileOpen ? t('nav.close_menu') : t('nav.open_menu')"
          :aria-expanded="mobileOpen"
          data-testid="mobile-toggle"
          @click="mobileOpen = !mobileOpen"
        >
          <X v-if="mobileOpen" :size="22" />
          <MenuIcon v-else :size="22" />
        </button>
      </div>

      <!-- Mobile nav -->
      <nav
        v-if="mobileOpen"
        class="border-t border-surface-200 bg-white md:hidden"
        data-testid="nav-mobile"
      >
        <div class="page-container flex flex-col gap-2 py-3">
          <template v-if="!isAuthenticated">
            <RouterLink to="/login" class="block" @click="mobileOpen = false">
              <Button :label="t('nav.login')" severity="secondary" outlined fluid />
            </RouterLink>
            <RouterLink to="/register" class="block" @click="mobileOpen = false">
              <Button :label="t('nav.register')" severity="primary" fluid />
            </RouterLink>
          </template>
          <template v-else>
            <div class="flex items-center gap-3 py-2">
              <Avatar :label="initials" shape="circle" class="bg-primary-600 text-white" />
              <div class="leading-tight">
                <div class="text-sm font-semibold text-surface-900">{{ fullName }}</div>
                <div v-if="user" class="text-xs text-surface-500">{{ user.email }}</div>
              </div>
            </div>
            <RouterLink
              v-for="link in roleNavLinks"
              :key="link.to"
              :to="link.to"
              class="block rounded-lg px-3 py-2 text-sm font-medium text-surface-700 hover:bg-surface-100"
              @click="mobileOpen = false"
            >
              {{ t(link.labelKey) }}
            </RouterLink>
            <RouterLink
              to="/notifications"
              class="block rounded-lg px-3 py-2 text-sm font-medium text-surface-700 hover:bg-surface-100"
              @click="mobileOpen = false"
            >
              {{ t('nav.notifications') }}
            </RouterLink>
            <Button
              :label="t('nav.logout')"
              severity="secondary"
              outlined
              fluid
              icon="pi pi-sign-out"
              @click="doLogout"
            />
          </template>
        </div>
      </nav>
    </header>

    <main class="flex-1">
      <slot />
    </main>

    <footer class="border-t border-surface-200 bg-white">
      <div class="page-container py-6 text-center text-sm text-surface-500">
        © 2026 Externist-Portal · Externistenprüfungskommission
      </div>
    </footer>
  </div>
</template>
